import type { Role } from '@repo/shared-types';
import { DEFAULT_TAX_RATE } from '@repo/shared-types';
import Decimal from 'decimal.js';
import type { QuoteOutput } from '../types/calculator.types';

/**
 * calculator-math — the single source of truth for the FE quote
 * calculator (Slice 8 PR#1, polish PR installment, polish PR term-input).
 * Pure, side-effect free, decimal.js powered so money math never drifts
 * (e.g. 1000/3 → 333.333333 with no float error).
 *
 * The BE parity endpoint `POST /api/v1/simulate/quote`
 * (`apps/api/src/routes/simulate.ts`) mirrors this exact formula and
 * the same `tasaMensual`, so FE and BE agree byte-for-byte.
 *
 * ROLE GATING (design obs-443 §E.3): `margin` is only computed for
 * `role !== 'WAREHOUSE'`. WAREHOUSE gets `null` regardless of whether
 * cost data leaked into the inputs (defense in depth against cost-leak).
 * The `installment` figure is derived from `total` and is therefore
 * returned for ALL roles — it carries no cost information.
 *
 * RATE (polish PR#1): the IVA rate (19%) lives in
 * `@repo/shared-types/tax` as `DEFAULT_TAX_RATE`. The legacy
 * `QuotationForm` uses a 22% constant in the same module — see
 * `packages/shared-types/src/tax.ts` for the reconciliation plan once
 * the business picks a single rate.
 *
 * FINANCING (polish PR term-input): replaced the closed `FINANCING_TERMS`
 * lookup with a continuous `tasaMensual(meses)` — the user types a
 * free-form term (any positive integer of months) and the monthly rate
 * is interpolated linearly between `RATE_TERM_MIN_MONTHS=3 → 3.0%` and
 * `RATE_TERM_MAX_MONTHS=60 → 5.5%`. The closed-table was dropped in
 * favor of "mayor versatilidad" per UX feedback; the 8 prior points
 * (3/6/12/18/24/36/48/60) no longer all sit on the rate curve by design.
 * The BE simulate route mirrors this same function — any change here
 * must be reflected in `apps/api/src/routes/simulate.ts`.
 */

// Match the BE decimal.js config: 18 significant digits, ROUND_HALF_UP.
Decimal.set({ precision: 18, rounding: Decimal.ROUND_HALF_UP });

/**
 * Local alias for `DEFAULT_TAX_RATE` (19%, Slice 8 calculator spec).
 *
 * Kept as a re-export so callers (the unit test in
 * `apps/web/src/__tests__/calculator-math.test.ts`) that import
 * `TAX_RATE` from this module keep working without churn. New code
 * should import `DEFAULT_TAX_RATE` from `@repo/shared-types/tax`
 * directly so the shared-types module stays the SSOT.
 */
export const TAX_RATE = DEFAULT_TAX_RATE;

/** Working precision for the returned numbers (money, 6 dp). */
const MONEY_DP = 6;

/**
 * Financing rate curve anchors (polish PR term-input, continuous model).
 *
 *   `tasaMensual(3m)  = 0.030` (3.0%)
 *   `tasaMensual(60m) = 0.055` (5.5%)
 *
 * Linear in between (and extrapolated linearly outside the anchor range
 * if the user types something like 1 or 240 — the function never throws,
 * but the UI clamps the input to [1, 120] months for usability).
 */
export const RATE_TERM_MIN_MONTHS = 3;
export const RATE_TERM_MAX_MONTHS = 60;
export const RATE_AT_MIN = new Decimal('0.030');
export const RATE_AT_MAX = new Decimal('0.055');

/** Default financing term (months) when the modal first opens. */
export const FINANCING_DEFAULT_TERM_MONTHS = 12;

/**
 * Continuous monthly financing rate for any positive term.
 *
 *   t(m) = RATE_AT_MIN + (m − RATE_TERM_MIN_MONTHS) × slope
 *   slope = (RATE_AT_MAX − RATE_AT_MIN) / (RATE_TERM_MAX_MONTHS − RATE_TERM_MIN_MONTHS)
 *
 * For m ≤ 0 returns `Decimal(0)` so the caller short-circuits the
 * installment formula to the principal/n fallback (the UI never passes
 * this — it min-clamps the input).
 */
export function tasaMensual(meses: number): Decimal {
  if (!Number.isFinite(meses) || meses <= 0) return new Decimal(0);
  const slope = RATE_AT_MAX.minus(RATE_AT_MIN).dividedBy(
    RATE_TERM_MAX_MONTHS - RATE_TERM_MIN_MONTHS,
  );
  return RATE_AT_MIN.plus(new Decimal(meses - RATE_TERM_MIN_MONTHS).times(slope));
}

/**
 * French amortization — monthly installment `P` for a principal `M`
 * financed over `n` months at monthly rate `i` (decimal):
 *
 *   P = M × i × (1+i)^n / ((1+i)^n − 1)
 *
 * When `i = 0`, falls back to `M / n`. Returns `null` when the principal
 * is non-positive or `meses ≤ 0` (no silent fallback for invalid input).
 * Any positive integer of months is supported via `tasaMensual`.
 */
export function computeMonthlyInstallment(
  principal: number | string | Decimal,
  meses: number,
): number | null {
  const principalD = toDecimal(principal);
  if (principalD.isZero() || principalD.isNegative() || meses <= 0) return null;
  const tasa = tasaMensual(meses);
  if (tasa.isZero()) {
    return round(principalD.dividedBy(meses));
  }
  const onePlusI = new Decimal(1).plus(tasa);
  const factor = onePlusI.pow(meses);
  const monthly = principalD.times(tasa).times(factor).dividedBy(factor.minus(1));
  return round(monthly);
}

export interface ComputeQuoteParams {
  /** Product default sale price (decimal string or number). */
  unitPrice: number | string;
  /** Product default cost — only present for ADMIN/SELLER. */
  unitCost?: number | string | null;
  quantity: number | string;
  discountPercent: number | string;
  exchangeRate: number | string;
  includeTax: boolean;
  /** Financing term in months — fed to `tasaMensual` (continuous). */
  termMonths?: number;
  role: Role;
}

function toDecimal(
  value: number | string | Decimal | null | undefined,
  fallback = '0',
): Decimal {
  if (value === null || value === undefined || value === '') return new Decimal(fallback);
  if (value instanceof Decimal) return value.isFinite() ? value : new Decimal(fallback);
  const d = new Decimal(String(value));
  return d.isFinite() ? d : new Decimal(fallback);
}

function round(value: Decimal): number {
  return value.toDecimalPlaces(MONEY_DP, Decimal.ROUND_HALF_UP).toNumber();
}

/**
 * Compute the full quote output from resolved numeric inputs.
 *
 * Pipeline:
 *   subtotal      = unitPrice * quantity
 *   discountAmount= subtotal * discountPercent / 100
 *   taxableBase   = subtotal - discountAmount
 *   taxAmount     = includeTax ? taxableBase * TAX_RATE : 0
 *   total         = taxableBase + taxAmount
 *   totalInBase   = total * exchangeRate
 *   margin%       = (taxableBase - unitCost*qty) / taxableBase * 100   (role-gated)
 *   installment   = French amortization on `total` for `termMonths` using
 *                   the continuous `tasaMensual` rate curve (all roles;
 *                   null when total <= 0 or termMonths is non-positive).
 */
export function computeQuoteOutput(params: ComputeQuoteParams): QuoteOutput {
  const unitPrice = toDecimal(params.unitPrice);
  const quantity = toDecimal(params.quantity);
  const discountPercent = toDecimal(params.discountPercent);
  const exchangeRate = toDecimal(params.exchangeRate, '1');

  const subtotal = unitPrice.times(quantity);
  const discountAmount = subtotal.times(discountPercent).dividedBy(100);
  const taxableBase = subtotal.minus(discountAmount);
  const taxAmount = params.includeTax ? taxableBase.times(TAX_RATE) : new Decimal(0);
  const total = taxableBase.plus(taxAmount);
  const totalInBaseCurrency = total.times(exchangeRate);

  const canSeeCosts = params.role !== 'WAREHOUSE';

  // Margin — only when cost is available AND the role may see it.
  let margin: number | null = null;
  if (
    canSeeCosts &&
    params.unitCost !== null &&
    params.unitCost !== undefined &&
    params.unitCost !== ''
  ) {
    const costTotal = toDecimal(params.unitCost).times(quantity);
    if (taxableBase.isZero()) {
      margin = 0;
    } else {
      margin = round(taxableBase.minus(costTotal).dividedBy(taxableBase).times(100));
    }
  }

  // Installment — continuous `tasaMensual(meses)` curve, French
  // amortization on the final `total` (incl. IVA when applicable).
  // Returned for all roles because it depends only on `total` +
  // `termMonths` (no cost).
  const installment =
    params.termMonths !== undefined && params.termMonths !== null
      ? computeMonthlyInstallment(total, params.termMonths)
      : null;

  return {
    subtotal: round(subtotal),
    discountAmount: round(discountAmount),
    taxableBase: round(taxableBase),
    taxAmount: round(taxAmount),
    total: round(total),
    totalInBaseCurrency: round(totalInBaseCurrency),
    margin,
    installment,
  };
}

/** An all-zero output (used when no product is selected yet). */
export function emptyQuoteOutput(role: Role): QuoteOutput {
  return computeQuoteOutput({
    unitPrice: 0,
    quantity: 0,
    discountPercent: 0,
    exchangeRate: 1,
    includeTax: false,
    termMonths: FINANCING_DEFAULT_TERM_MONTHS,
    role,
  });
}
